import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PortalAdminLayout from "./PortalAdminLayout";
import "./PortalDashboard.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const CountryWaitlistManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({
    countryCounts: [],
    interestedInCounts: [],
    totalEntries: 0,
  });
  const [conversionStats, setConversionStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [interestedInFilter, setInterestedInFilter] = useState("");
  const [notifiedFilter, setNotifiedFilter] = useState("");
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const checkPortalAdmin = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return false;
    }

    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(""),
      );

      const decoded = JSON.parse(jsonPayload);
      const hasPortalAdminAccess =
        decoded.role === "portal_admin" ||
        decoded.role === "R6" ||
        (decoded.roles &&
          (decoded.roles.includes("portal_admin") ||
            decoded.roles.includes("R6")));

      if (!hasPortalAdminAccess) {
        navigate("/portal-admin");
        return false;
      }
      return true;
    } catch (error) {
      navigate("/login");
      return false;
    }
  }, [navigate]);

  const fetchEntries = useCallback(
    async (page = 1) => {
      const token = localStorage.getItem("ogestyle_token");
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams({ page, limit: 50 });
        if (search) params.append("search", search);
        if (countryFilter) params.append("country", countryFilter);
        if (interestedInFilter)
          params.append("interestedIn", interestedInFilter);
        if (notifiedFilter) params.append("notified", notifiedFilter);

        const response = await axios.get(
          `${API_BASE_URL}/api/admin/country-waitlist?${params}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        setEntries(response.data.entries);
        setPagination(response.data.pagination);
        setStats(response.data.stats);
      } catch (error) {
        setError("Failed to load country waitlist entries");
      } finally {
        setLoading(false);
      }
    },
    [search, countryFilter, interestedInFilter, notifiedFilter],
  );

  const fetchConversionStats = useCallback(async () => {
    const token = localStorage.getItem("ogestyle_token");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/country-waitlist/conversion-stats`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setConversionStats(response.data);
    } catch (error) {
      // Don't show error, just don't display conversion stats
    }
  }, []);

  useEffect(() => {
    if (checkPortalAdmin()) {
      fetchEntries();
      fetchConversionStats();
    }
  }, [checkPortalAdmin, fetchEntries, fetchConversionStats]);

  const handleExport = async () => {
    const token = localStorage.getItem("ogestyle_token");
    try {
      const params = new URLSearchParams();
      if (countryFilter) params.append("country", countryFilter);
      if (interestedInFilter) params.append("interestedIn", interestedInFilter);

      const response = await axios.get(
        `${API_BASE_URL}/api/admin/country-waitlist/export?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `country-waitlist-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setSuccessMessage("Export completed successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      setError("Failed to export waitlist");
    }
  };

  const handleNotify = async (entryId) => {
    const token = localStorage.getItem("ogestyle_token");
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/country-waitlist/${entryId}/notify`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setSuccessMessage("Entry marked as notified!");
      setTimeout(() => setSuccessMessage(""), 3000);
      fetchEntries(pagination.page);
    } catch (error) {
      setError("Failed to update entry");
    }
  };

  const handleBatchNotify = async () => {
    if (selectedEntries.length === 0) {
      setError("Please select entries to notify");
      return;
    }

    const token = localStorage.getItem("ogestyle_token");
    try {
      const emails = selectedEntries
        .map((id) => {
          const entry = entries.find((e) => e._id === id);
          return entry?.email;
        })
        .filter(Boolean);

      await axios.post(
        `${API_BASE_URL}/api/admin/country-waitlist/notify-batch`,
        { emails },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setSuccessMessage(
        `${selectedEntries.length} entries marked as notified!`,
      );
      setTimeout(() => setSuccessMessage(""), 3000);
      setSelectedEntries([]);
      fetchEntries(pagination.page);
    } catch (error) {
      setError("Failed to notify entries");
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) {
      return;
    }

    const token = localStorage.getItem("ogestyle_token");
    try {
      await axios.delete(
        `${API_BASE_URL}/api/admin/country-waitlist/${entryId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setSuccessMessage("Entry deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
      fetchEntries(pagination.page);
    } catch (error) {
      setError("Failed to delete entry");
    }
  };

  const toggleSelectEntry = (entryId) => {
    setSelectedEntries((prev) =>
      prev.includes(entryId)
        ? prev.filter((id) => id !== entryId)
        : [...prev, entryId],
    );
  };

  const toggleSelectAll = () => {
    if (selectedEntries.length === entries.length) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries(entries.map((e) => e._id));
    }
  };

  const countryNames = {
    US: "United States",
    CA: "Canada",
    GB: "United Kingdom",
    NG: "Nigeria",
  };

  const interestedInLabels = {
    customer: "Customer",
    business_owner: "Business Owner",
    freelancer: "Freelancer",
    other: "Other",
  };

  return (
    <PortalAdminLayout>
      <div className="portal-dashboard">
        <div className="dashboard-header">
          <div>
            <h1>Country Expansion Waitlist</h1>
            <p style={{ color: "#6b7280", marginTop: "8px" }}>
              Manage international leads and track expansion opportunities
            </p>
          </div>
          <button
            onClick={handleExport}
            style={{
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              color: "white",
              padding: "12px 24px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "14px",
            }}
          >
            📥 Export to CSV
          </button>
        </div>

        {/* Stats Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "20px",
            marginBottom: "30px",
          }}
        >
          <div
            style={{
              background: "white",
              padding: "20px",
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                color: "#6b7280",
                marginBottom: "8px",
              }}
            >
              Total Entries
            </div>
            <div
              style={{ fontSize: "32px", fontWeight: "700", color: "#1a659e" }}
            >
              {stats.totalEntries}
            </div>
          </div>

          {stats.countryCounts.map((country) => (
            <div
              key={country._id}
              style={{
                background: "white",
                padding: "20px",
                borderRadius: "12px",
                border: "1px solid #e5e7eb",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  marginBottom: "8px",
                }}
              >
                {countryNames[country._id] || country._id}
              </div>
              <div
                style={{
                  fontSize: "32px",
                  fontWeight: "700",
                  color: "#2d6da3",
                }}
              >
                {country.count}
              </div>
              <div
                style={{ fontSize: "11px", color: "#10b981", marginTop: "4px" }}
              >
                {country.notified} notified
              </div>
            </div>
          ))}
        </div>

        {/* Interest Type Breakdown */}
        <div
          style={{
            background: "white",
            padding: "20px",
            borderRadius: "12px",
            border: "1px solid #e5e7eb",
            marginBottom: "20px",
          }}
        >
          <h3
            style={{
              fontSize: "14px",
              fontWeight: "600",
              marginBottom: "16px",
            }}
          >
            Interest Type Breakdown
          </h3>
          <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
            {stats.interestedInCounts.map((type) => (
              <div
                key={type._id}
                style={{
                  padding: "12px 20px",
                  background: "#f9fafb",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                }}
              >
                <span
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    marginRight: "8px",
                  }}
                >
                  {interestedInLabels[type._id] || type._id}:
                </span>
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#111827",
                  }}
                >
                  {type.count}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Conversion Stats */}
        {conversionStats &&
          conversionStats.stats &&
          conversionStats.stats.length > 0 && (
            <div
              style={{
                background: "white",
                padding: "20px",
                borderRadius: "12px",
                border: "1px solid #e5e7eb",
                marginBottom: "20px",
              }}
            >
              <h3
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "16px",
                  color: "#059669",
                }}
              >
                📊 Conversion Tracking (Waitlist → Signups)
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: "15px",
                }}
              >
                {conversionStats.stats.map((countryStat) => (
                  <div
                    key={countryStat.country}
                    style={{
                      padding: "16px",
                      background: "#f0fdf4",
                      borderRadius: "8px",
                      border: "1px solid #bbf7d0",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#065f46",
                        marginBottom: "12px",
                      }}
                    >
                      {countryNames[countryStat.country] || countryStat.country}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "6px",
                      }}
                    >
                      <span style={{ fontSize: "12px", color: "#6b7280" }}>
                        Total Waitlist:
                      </span>
                      <span style={{ fontSize: "12px", fontWeight: "600" }}>
                        {countryStat.total}
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "6px",
                      }}
                    >
                      <span style={{ fontSize: "12px", color: "#6b7280" }}>
                        Notified:
                      </span>
                      <span style={{ fontSize: "12px", fontWeight: "600" }}>
                        {countryStat.notified}
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "10px",
                      }}
                    >
                      <span style={{ fontSize: "12px", color: "#059669" }}>
                        Converted:
                      </span>
                      <span
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "#059669",
                        }}
                      >
                        {countryStat.converted}
                      </span>
                    </div>

                    <div
                      style={{
                        padding: "8px",
                        background: "#dcfce7",
                        borderRadius: "6px",
                        textAlign: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#065f46",
                          marginBottom: "2px",
                        }}
                      >
                        Conversion Rate
                      </div>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "700",
                          color: "#059669",
                        }}
                      >
                        {countryStat.conversionRate?.toFixed(1) ?? "0.0"}%
                      </div>
                    </div>

                    {(countryStat.customers > 0 ||
                      countryStat.businesses > 0 ||
                      countryStat.freelancers > 0) && (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#6b7280",
                          marginTop: "8px",
                        }}
                      >
                        {countryStat.customers > 0 && (
                          <div>• {countryStat.customers} customers</div>
                        )}
                        {countryStat.businesses > 0 && (
                          <div>• {countryStat.businesses} businesses</div>
                        )}
                        {countryStat.freelancers > 0 && (
                          <div>• {countryStat.freelancers} freelancers</div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {conversionStats.summary && (
                <div
                  style={{
                    marginTop: "15px",
                    paddingTop: "15px",
                    borderTop: "1px solid #e5e7eb",
                    fontSize: "12px",
                    color: "#6b7280",
                  }}
                >
                  <strong>Overall Summary:</strong>{" "}
                  {conversionStats.summary.totalConverted} conversions out of{" "}
                  {conversionStats.summary.totalNotified} notified (
                  {conversionStats.summary.overallConversionRate?.toFixed(1) ??
                    "0.0"}
                  % conversion rate)
                </div>
              )}
            </div>
          )}

        {/* Messages */}
        {error && (
          <div
            style={{
              padding: "12px 20px",
              background: "#fee2e2",
              color: "#991b1b",
              borderRadius: "8px",
              marginBottom: "20px",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        )}

        {successMessage && (
          <div
            style={{
              padding: "12px 20px",
              background: "#d1fae5",
              color: "#065f46",
              borderRadius: "8px",
              marginBottom: "20px",
              fontSize: "14px",
            }}
          >
            {successMessage}
          </div>
        )}

        {/* Filters */}
        <div
          style={{
            background: "white",
            padding: "20px",
            borderRadius: "12px",
            border: "1px solid #e5e7eb",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px",
            }}
          >
            <input
              type="text"
              placeholder="Search by name, email, or city..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                padding: "10px 14px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            />

            <select
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              style={{
                padding: "10px 14px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            >
              <option value="">All Countries</option>
              <option value="CA">Canada</option>
              <option value="GB">United Kingdom</option>
              <option value="NG">Nigeria</option>
              <option value="US">United States</option>
            </select>

            <select
              value={interestedInFilter}
              onChange={(e) => setInterestedInFilter(e.target.value)}
              style={{
                padding: "10px 14px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            >
              <option value="">All Types</option>
              <option value="customer">Customers</option>
              <option value="business_owner">Business Owners</option>
              <option value="freelancer">Freelancers</option>
              <option value="other">Other</option>
            </select>

            <select
              value={notifiedFilter}
              onChange={(e) => setNotifiedFilter(e.target.value)}
              style={{
                padding: "10px 14px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            >
              <option value="">All Statuses</option>
              <option value="false">Not Notified</option>
              <option value="true">Notified</option>
            </select>
          </div>
        </div>

        {/* Batch Actions */}
        {selectedEntries.length > 0 && (
          <div
            style={{
              background: "#f0f9ff",
              padding: "12px 20px",
              borderRadius: "8px",
              border: "1px solid #bae6fd",
              marginBottom: "20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ fontSize: "14px", color: "#0c4a6e" }}>
              {selectedEntries.length} entries selected
            </span>
            <button
              onClick={handleBatchNotify}
              style={{
                background: "#0ea5e9",
                color: "white",
                padding: "8px 16px",
                borderRadius: "6px",
                border: "none",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "600",
              }}
            >
              Mark as Notified
            </button>
          </div>
        )}

        {/* Entries Table */}
        <div className="analytics-card">
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                Loading entries...
              </div>
            </div>
          ) : entries.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                No entries found
              </div>
            </div>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                      <th style={{ padding: "12px", textAlign: "left" }}>
                        <input
                          type="checkbox"
                          checked={selectedEntries.length === entries.length}
                          onChange={toggleSelectAll}
                        />
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#6b7280",
                        }}
                      >
                        Name
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#6b7280",
                        }}
                      >
                        Email
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#6b7280",
                        }}
                      >
                        Country
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#6b7280",
                        }}
                      >
                        Interest
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#6b7280",
                        }}
                      >
                        Business Type
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#6b7280",
                        }}
                      >
                        City
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#6b7280",
                        }}
                      >
                        Source
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#6b7280",
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#6b7280",
                        }}
                      >
                        Created
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#6b7280",
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry) => (
                      <tr
                        key={entry._id}
                        style={{ borderBottom: "1px solid #f3f4f6" }}
                      >
                        <td style={{ padding: "12px" }}>
                          <input
                            type="checkbox"
                            checked={selectedEntries.includes(entry._id)}
                            onChange={() => toggleSelectEntry(entry._id)}
                          />
                        </td>
                        <td style={{ padding: "12px", fontSize: "13px" }}>
                          {entry.name}
                        </td>
                        <td style={{ padding: "12px", fontSize: "13px" }}>
                          <a
                            href={`mailto:${entry.email}`}
                            style={{ color: "#2563eb", textDecoration: "none" }}
                          >
                            {entry.email}
                          </a>
                        </td>
                        <td style={{ padding: "12px", fontSize: "13px" }}>
                          <span
                            style={{
                              padding: "4px 8px",
                              background: "#dbeafe",
                              color: "#1e40af",
                              borderRadius: "4px",
                              fontSize: "11px",
                              fontWeight: "600",
                            }}
                          >
                            {countryNames[entry.country] || entry.country}
                          </span>
                        </td>
                        <td style={{ padding: "12px", fontSize: "13px" }}>
                          {interestedInLabels[entry.interestedIn] ||
                            entry.interestedIn}
                        </td>
                        <td style={{ padding: "12px", fontSize: "13px" }}>
                          {entry.businessType || "-"}
                        </td>
                        <td style={{ padding: "12px", fontSize: "13px" }}>
                          {entry.city || "-"}
                        </td>
                        <td style={{ padding: "12px", fontSize: "13px" }}>
                          {entry.source || "-"}
                        </td>
                        <td style={{ padding: "12px", fontSize: "13px" }}>
                          {entry.notified ? (
                            <span
                              style={{ color: "#10b981", fontWeight: "600" }}
                            >
                              ✓ Notified
                            </span>
                          ) : (
                            <span style={{ color: "#6b7280" }}>Pending</span>
                          )}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            fontSize: "13px",
                            color: "#6b7280",
                          }}
                        >
                          {new Date(entry.createdAt).toLocaleDateString()}
                        </td>
                        <td style={{ padding: "12px" }}>
                          <div style={{ display: "flex", gap: "8px" }}>
                            {!entry.notified && (
                              <button
                                onClick={() => handleNotify(entry._id)}
                                style={{
                                  padding: "6px 12px",
                                  background: "#10b981",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  fontSize: "11px",
                                  fontWeight: "600",
                                }}
                              >
                                Notify
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(entry._id)}
                              style={{
                                padding: "6px 12px",
                                background: "#ef4444",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontSize: "11px",
                                fontWeight: "600",
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "12px",
                    marginTop: "24px",
                    paddingTop: "24px",
                    borderTop: "1px solid #e5e7eb",
                  }}
                >
                  <button
                    onClick={() => fetchEntries(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    style={{
                      padding: "8px 16px",
                      background: pagination.page === 1 ? "#f3f4f6" : "#2d6da3",
                      color: pagination.page === 1 ? "#9ca3af" : "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: pagination.page === 1 ? "not-allowed" : "pointer",
                      fontSize: "13px",
                      fontWeight: "600",
                    }}
                  >
                    Previous
                  </button>

                  <span style={{ fontSize: "13px", color: "#6b7280" }}>
                    Page {pagination.page} of {pagination.pages}
                  </span>

                  <button
                    onClick={() => fetchEntries(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    style={{
                      padding: "8px 16px",
                      background:
                        pagination.page === pagination.pages
                          ? "#f3f4f6"
                          : "#2d6da3",
                      color:
                        pagination.page === pagination.pages
                          ? "#9ca3af"
                          : "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor:
                        pagination.page === pagination.pages
                          ? "not-allowed"
                          : "pointer",
                      fontSize: "13px",
                      fontWeight: "600",
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PortalAdminLayout>
  );
};

export default CountryWaitlistManagement;
